import React from 'react';
import type { VideoComponent } from '../../../types/builder';
import { resolveVideoSrc } from '../../../utils/videoEmbed';

interface VideoRendererProps {
  component: VideoComponent;
}

const frameClass: Record<VideoComponent['settings']['style'], string> = {
  default: 'mx-auto',
  rounded: 'mx-auto rounded-2xl overflow-hidden',
  card: 'rounded-xl',
  theater: 'shadow-2xl',
};

export const VideoRenderer: React.FC<VideoRendererProps> = ({ component }) => {
  const { settings } = component;
  const style = settings.style || 'default';
  const resolved = resolveVideoSrc(settings.src, {
    autoplay: settings.autoplay,
    loop: settings.loop,
    controls: settings.controls,
  });

  const mediaStyle: React.CSSProperties = {
    width: style === 'card' || style === 'theater' ? '100%' : settings.width,
    height: settings.height,
    border: 0,
    display: 'block',
  };

  const media =
    resolved.kind === 'iframe' ? (
      <iframe
        src={resolved.src}
        style={mediaStyle}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen
        referrerPolicy="strict-origin-when-cross-origin"
        title="Video"
        className={frameClass[style]}
      />
    ) : resolved.kind === 'file' ? (
      <video
        src={resolved.src}
        style={mediaStyle}
        controls={settings.controls !== false}
        autoPlay={settings.autoplay}
        loop={settings.loop}
        muted={settings.autoplay}
        playsInline
        className={frameClass[style]}
      />
    ) : (
      <div
        className="flex items-center justify-center bg-gray-100 text-gray-400 text-sm"
        style={{ ...mediaStyle, minHeight: settings.height || 200 }}
      >
        วางลิงก์ YouTube, Shorts หรือไฟล์วิดีโอ
      </div>
    );

  if (style === 'card') {
    return (
      <div className="p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-4 max-w-4xl mx-auto">{media}</div>
      </div>
    );
  }

  if (style === 'theater') {
    return (
      <div className="relative bg-black py-8">
        <div className="max-w-6xl mx-auto px-4">{media}</div>
      </div>
    );
  }

  return <div className="p-4">{media}</div>;
};
